import { useCallback } from 'react';
import { useImpersonationActions } from './ImpersonationContext';
import { useNotifications } from '../NotificationContext';
import { ImpersonationTarget } from '../../types/models/ImpersonationTarget';

interface UseImpersonationWithNotificationsReturn {
  /** Begin impersonating the given target, then hard-reload to '/'. */
  startWithNotification: (target: ImpersonationTarget) => void;

  /** Stop impersonating, then hard-reload to '/'. */
  stopWithNotification: () => void;
}

/**
 * Higher-level hook composing impersonation actions with a hard navigation
 * to '/' on success.
 *
 * The full-page navigation is intentional: the React tree holds per-tenant
 * data (table caches, fetched lists, modal state) that would otherwise be
 * scoped to the wrong identity after a switch. Reloading guarantees a clean
 * tree and prevents cross-tenant data leakage.
 *
 * Why '/' instead of reloading the current URL: the operator's previous URL
 * may reference resources the new identity cannot see (e.g.
 * /datacenter/servers/42), which would 404 or — worse — silently load a
 * different tenant's resource that happens to share that ID.
 *
 * Why no success toast: the navigation wipes any pending toast immediately,
 * and the visibly-different post-reload state is sufficient feedback.
 *
 * Persistence of the impersonation target is handled by ImpersonationContext
 * (sessionStorage), so the target survives the reload.
 *
 * For programmatic / non-UI callers that want to change state without
 * reloading, use `useImpersonationActions` directly.
 */
export function useImpersonationWithNotifications(): UseImpersonationWithNotificationsReturn {
  const { startImpersonation, stopImpersonation } = useImpersonationActions();
  const { addNotification, setDrawerOpen } = useNotifications();

  const startWithNotification = useCallback(
    (newTarget: ImpersonationTarget) => {
      try {
        startImpersonation(newTarget);
        window.location.assign('/');
      } catch (err) {
        addNotification({
          title: 'Could not start impersonation',
          srTitle: 'Failed to start impersonation',
          variant: 'danger',
          description: err instanceof Error ? err.message : String(err),
        });
        setDrawerOpen(true);
      }
    },
    [startImpersonation, addNotification, setDrawerOpen],
  );

  const stopWithNotification = useCallback(() => {
    try {
      stopImpersonation();
      window.location.assign('/');
    } catch (err) {
      addNotification({
        title: 'Could not stop impersonation',
        srTitle: 'Failed to stop impersonation',
        variant: 'danger',
        description: err instanceof Error ? err.message : String(err),
      });
      setDrawerOpen(true);
    }
  }, [stopImpersonation, addNotification, setDrawerOpen]);

  return { startWithNotification, stopWithNotification };
}
